import axios from "axios";
import { GroupUserRecord } from "models/view-models/groups/group-user-record";
import { ServiceHookFactory } from "utilities/services/service-hook-factory";
import { ServiceResponse, ServiceUtils } from "andculturecode-javascript-core";
import { useCallback } from "react";
import { useCancellablePromise } from "andculturecode-javascript-react";
import { EncodingType } from "../../../models/enumerations/encoding/encoding-type";
import { GroupUserUploadResultRecord } from "../../../models/view-models/groups/group-user-upload-result-record";
import { CreateBulkGroupUsersDto } from "../../../models/interfaces/groups/create-bulk-group-users-dto";
import { BulkGroupUsersSummaryRecord } from "../../../models/view-models/groups/bulk-group-users-summary-record";

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const baseEndpoint: string = "groupusers";
const resourceType: typeof GroupUserRecord = GroupUserRecord;
const resourceEndpoint = `${baseEndpoint}/:id`;
const csvPreviewEndpoint: string = `${baseEndpoint}/csvpreview/:encodingType/:fileId/:groupId`;
const bulkGroupUsersEndpoint: string = `${baseEndpoint}/:encodingType/bulkUpload`;

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Types
// -------------------------------------------------------------------------------------------------

type CreateGroupUserService = (
    params: CreateGroupUserParams
) => Promise<ServiceResponse<GroupUserRecord>>;

type CreateBulkGroupUserService = (
    encodingType: EncodingType,
    params: CreateBulkGroupUsersDto
) => Promise<ServiceResponse<BulkGroupUsersSummaryRecord>>;

// #endregion Types

// -------------------------------------------------------------------------------------------------
// #region enum
// -------------------------------------------------------------------------------------------------

// #endregion enum

export interface CreateGroupUserParams {
    groupId: number;
    userIds: number[];
}

export interface GroupUserResourcePathParams {
    id: number;
}
export interface ListGroupUsersRecordQueryParams {
    groupId?: number;
    searchText?: string;
    skip?: number;
    take?: number;
}

export interface GetCSVPreviewPathParams {
    encodingType: EncodingType;
    fileId: number;
    groupId: number;
}

export interface GetCSVPreviewQueryParams {
    hasHeader: boolean;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Service
// -------------------------------------------------------------------------------------------------

const GroupUsersService = {
    /**
     * Custom hook for leveraging service create calls in React components
     */
    useCreate: (): { create: CreateGroupUserService } => {
        const { cancellablePromise } = useCancellablePromise();

        const serviceCreate: CreateGroupUserService = (
            params: CreateGroupUserParams
        ): Promise<ServiceResponse<GroupUserRecord>> => {
            return axios
                .post(baseEndpoint, params)
                .then((r) => ServiceUtils.mapAxiosResponse(resourceType, r));
        };

        function create(params: CreateGroupUserParams): Promise<ServiceResponse<GroupUserRecord>> {
            return cancellablePromise(serviceCreate(params)) as Promise<
                ServiceResponse<GroupUserRecord>
            >;
        }

        return { create: useCallback(create, [cancellablePromise]) };
    },

    /**
     * Custom hook for leveraging service get calls in React components
     */
    useGet: ServiceHookFactory.useGet<GroupUserRecord, GroupUserResourcePathParams>(
        resourceType,
        resourceEndpoint
    ),

    /**
     * Custom hook for leveraging service index calls in React components
     */
    useList: ServiceHookFactory.useList<GroupUserRecord, ListGroupUsersRecordQueryParams>(
        resourceType,
        baseEndpoint
    ),

    /**
     * Custom hook for leveraging service delete calls in react components
     */
    useDelete: ServiceHookFactory.useDelete(resourceEndpoint),

    useGetCSVPreview: ServiceHookFactory.useNestedList<
        GroupUserUploadResultRecord,
        GetCSVPreviewPathParams,
        GetCSVPreviewQueryParams
    >(GroupUserUploadResultRecord, csvPreviewEndpoint),

    useBulkCreate: (): { create: CreateBulkGroupUserService } => {
        const { cancellablePromise } = useCancellablePromise();

        const serviceCreate: CreateBulkGroupUserService = (
            encodingType: EncodingType,
            params: CreateBulkGroupUsersDto
        ): Promise<ServiceResponse<BulkGroupUsersSummaryRecord>> => {
            const endPointWithEncodingTypeReplaced = bulkGroupUsersEndpoint.replace(
                ":encodingType",
                encodingType.toString()
            );

            return axios
                .post(endPointWithEncodingTypeReplaced, params)
                .then((r) => ServiceUtils.mapAxiosResponse(BulkGroupUsersSummaryRecord, r));
        };

        function create(
            encodingType: EncodingType,
            params: CreateBulkGroupUsersDto
        ): Promise<ServiceResponse<BulkGroupUsersSummaryRecord>> {
            return cancellablePromise(serviceCreate(encodingType, params)) as Promise<
                ServiceResponse<BulkGroupUsersSummaryRecord>
            >;
        }

        return { create: useCallback(create, [cancellablePromise]) };
    },
};

// #endregion Service

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { GroupUsersService };

// #endregion Exports
